import { useState, useEffect } from "react";
import { Card, Tag, Input, Button, Tooltip, Spin } from "antd";
import {
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileProtectOutlined,
  SaveOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  LockOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ELSTIC_URL } from "../../../../config";
import Swal from "sweetalert2";
import {
  useAddAttorneyNumbers, useDeleteAttorneyNumber,
} from "../../../hooks/useUserProfile";

// =============================================================================
// AttorneyDetails
//
// Props:
//   userData    — headerData (has attorney_numbers:[{attorney_number,tm_count,attorney_names}], total_tm_count)
//   isLoading   — boolean
//   planLimit   — number of attorneys the plan allows (from plan features)
//   userId      — string user id
//   isAttorney  — 1 | 0
// =============================================================================
const AttorneyDetails = ({
  userData,
  isLoading = false,
  planLimit = 0,
  userId,
  isAttorney,
}) => {
  const updateMutation = useAddAttorneyNumbers();
  const deleteMutation = useDeleteAttorneyNumber();
  /* Which row is in flight, so only its own button spins. A single boolean
     would grey out every row for one deletion. */
  const [deleting, setDeleting] = useState(null);

  // ── Existing (read-only) attorneys ───────────────────────────────────────
  const existing = Array.isArray(userData?.attorney_numbers)
    ? userData.attorney_numbers.map((item) => ({
        attorney_number: String(item?.attorney_number || ""),
        tm_count: Number(item?.tm_count || 0),
        attorney_names: Array.isArray(item?.attorney_names) ? item.attorney_names : [],
      }))
    : [];

  // ── Empty slots count ────────────────────────────────────────────────────
  const emptyCount = Math.max(0, planLimit - existing.length);

  const initSlots = (count) =>
    Array.from({ length: count }, () => ({
      value: "",
      status: null,
      message: "",
      tmCount: 0,
      isChecking: false,
    }));

  const [slots, setSlots] = useState(() => initSlots(emptyCount));

  // Name options + selections for new slots (keyed by slot index)
  const [slotNameOptions, setSlotNameOptions] = useState({}); // { [index]: [{attorney_name}] }
  const [slotSelectedNames, setSlotSelectedNames] = useState({}); // { [index]: string[] }

  // Auto-select when only one name available for a slot
  useEffect(() => {
    Object.entries(slotNameOptions).forEach(([idx, names]) => {
      if (names.length === 1) {
        setSlotSelectedNames((prev) => {
          if (prev[idx]?.length > 0) return prev;
          return { ...prev, [idx]: [names[0].attorney_name] };
        });
      }
    });
  }, [slotNameOptions]);

  // Re-sync slots when userData / planLimit changes
  useEffect(() => {
    const newCount = Math.max(0, planLimit - existing.length);
    setSlots(initSlots(newCount));
    setSlotNameOptions({});
    setSlotSelectedNames({});
  }, [planLimit, userData?.attorney_numbers?.length]);

  const totalTMCount = userData?.total_tm_count ?? 0;
  const isCorporate = isAttorney === 0 || isAttorney === "0";

  // ── Update a single slot field ───────────────────────────────────────────
  const patchSlot = (index, patch) => {
    setSlots((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
    );
  };

  /**
   * Remove a saved attorney number.
   *
   * Confirmed first, and the dialog says what actually goes with it: this
   * deletes the monitored trademarks and proprietors under that number, not
   * just the number. "Are you sure?" would not tell anyone that.
   *
   * No refetch afterwards — the response carries the remaining list and the new
   * total, and the hook patches the cached profile with them. The slots below
   * re-sync off that same change, so a half-typed row clears itself.
   */
  const confirmDelete = (atty) => {
    const count = Number(atty.tm_count || 0);
    Swal.fire({
      title: `Delete attorney number ${atty.attorney_number}?`,
      html: count > 0
        ? `<b>${count.toLocaleString("en-IN")}</b> monitored trademark${count === 1 ? "" : "s"}`
          + ` and the proprietors under this number will be permanently deleted.`
          + `<br/><span style="color:#8c8c8c;font-size:13px">This cannot be undone.</span>`
        : "This number and anything monitored under it will be permanently deleted."
          + `<br/><span style="color:#8c8c8c;font-size:13px">This cannot be undone.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setDeleting(atty.attorney_number);
      try {
        await deleteMutation.mutateAsync({
          userId,
          attorneyNumber: atty.attorney_number,
        });
      } catch {
        // The hook has already shown the server's own message.
      } finally {
        setDeleting(null);
      }
    });
  };

  const handleInputChange = (index, value) => {
    patchSlot(index, { value, status: null, message: "", tmCount: 0 });
    // Clear name options when number changes
    setSlotNameOptions((prev) => { const c = { ...prev }; delete c[index]; return c; });
    setSlotSelectedNames((prev) => { const c = { ...prev }; delete c[index]; return c; });
  };

  // ── Per-row Check Status ─────────────────────────────────────────────────
  const handleCheckStatus = async (index) => {
    const num = slots[index].value.trim();
    if (!num) {
      toast.warning("Please enter an attorney number first.");
      return;
    }

    patchSlot(index, { isChecking: true, status: null, message: "" });

    const applyNames = (detail) => {
      if (detail?.attorney_names?.length) {
        setSlotNameOptions((prev) => ({
          ...prev,
          [index]: detail.attorney_names.map((n) =>
            typeof n === "string" ? { attorney_name: n } : n
          ),
        }));
      }
    };

    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/attorney/get-attorney-status-count?attorney_number=${num}`
      );

      const { status, data } = res.data;

      if (status === 200 || status === 409) {
        const validAttorneys = data?.valid_attorneys || [];
        const details = data?.details || [];
        const detail = details.find((d) => d.attorney_number === num);

        if (validAttorneys.includes(num) && detail?.exists) {
          const count = Number(detail.count || 0);
          patchSlot(index, { status: "valid", message: `Valid — ${count} TMs`, tmCount: count, isChecking: false });
          applyNames(detail);
          toast.success(`Attorney ${num} verified successfully.`);
        } else {
          patchSlot(index, { status: "invalid", message: "Invalid Attorney Number", tmCount: 0, isChecking: false });
          toast.warning(`Attorney ${num} is invalid.`);
        }
      }
    } catch (err) {
      if (err.response?.status === 409) {
        const errBody = err.response.data;
        const errMsg = errBody?.message || "";
        const eData = errBody?.data || {};
        const validAttorneys = eData?.valid_attorneys || [];
        const details = eData?.details || [];

        if (details.length === 0) {
          patchSlot(index, { status: "invalid", message: errMsg || "Attorney Number already assigned", tmCount: 0, isChecking: false });
          toast.error(errMsg || "Attorney Number already assigned");
          return;
        }

        const detail = details.find((d) => d.attorney_number === num);
        if (validAttorneys.includes(num) && detail?.exists) {
          const count = Number(detail.count || 0);
          patchSlot(index, { status: "valid", message: `Valid — ${count} TMs`, tmCount: count, isChecking: false });
          applyNames(detail);
        } else {
          patchSlot(index, { status: "invalid", message: "Invalid Attorney Number", tmCount: 0, isChecking: false });
          toast.warning(errMsg || "Attorney number is invalid.");
        }
      } else {
        patchSlot(index, { isChecking: false });
        toast.error(err.response?.data?.message || "Failed to check attorney status.");
      }
    }
  };

  // ── Save all valid new attorneys ─────────────────────────────────────────
  const validNewSlots = slots.filter((s) => s.status === "valid" && s.value.trim());

  const handleSave = async () => {
    if (validNewSlots.length === 0) {
      toast.warning("No valid new attorney numbers to save.");
      return;
    }

    // Check if any valid slot with name options has no name selected
    const unselected = slots
      .map((s, i) => ({ s, i }))
      .filter(({ s, i }) => s.status === "valid" && s.value.trim() && slotNameOptions[i]?.length > 0 && !(slotSelectedNames[i]?.length > 0))
      .map(({ s }) => s.value.trim());

    if (unselected.length > 0) {
      toast.warning(`Please select an attorney name for: ${unselected.join(", ")}`);
      return;
    }

    const attorneyNumbers = slots
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.status === "valid" && s.value.trim())
      .map(({ s, i }) => ({
        number: s.value.trim(),
        tm_count: s.tmCount ? String(s.tmCount) : null,
        attorney_names: slotSelectedNames[i] || [],
      }));

    try {
      await updateMutation.mutateAsync({ userId, attorneyNumbers });
    } catch (_) {
      // error toast handled in hook
    }
  };

  // ── Name chip renderer ───────────────────────────────────────────────────
  const renderNameChips = (names, selectedArr, onToggle) => {
    const hasSelection = (selectedArr?.length || 0) > 0;
    return (
      <div style={{
        marginTop: 6,
        padding: "6px 8px",
        borderRadius: 6,
        border: `1.5px solid ${hasSelection ? "#b7eb8f" : "#ff4d4f"}`,
        background: hasSelection ? "#f6ffed" : "#fff1f0",
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: hasSelection ? "#389e0d" : "#cf1322", marginBottom: 4 }}>
          {hasSelection ? "✓ Name Selected" : "⚠ Select Name:"}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {names.map((opt) => {
            const isSel = (selectedArr || []).includes(opt.attorney_name);
            return (
              <div
                key={opt.attorney_name}
                onClick={onToggle ? () => onToggle(opt.attorney_name) : undefined}
                style={{
                  cursor: onToggle ? "pointer" : "default",
                  padding: "3px 8px",
                  borderRadius: 5,
                  fontSize: 11,
                  fontWeight: isSel ? 600 : 400,
                  border: `1.5px solid ${isSel ? "#52c41a" : "#d9d9d9"}`,
                  background: isSel ? "#f6ffed" : "#fafafa",
                  color: isSel ? "#389e0d" : "#595959",
                }}
              >
                {opt.attorney_name}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Card className="shadow-sm mb-4" style={{ borderRadius: 12 }}>
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  // ── Styles ───────────────────────────────────────────────────────────────
  const gridCols = "50px 1fr 130px 120px 180px";

  const headerRowStyle = {
    display: "grid",
    gridTemplateColumns: gridCols,
    padding: "10px 16px",
    background: "#f5f5f5",
    borderRadius: "6px 6px 0 0",
    fontWeight: 600,
    fontSize: 13,
    color: "#595959",
    borderBottom: "1px solid #e8e8e8",
  };

  const rowStyle = (bg) => ({
    display: "grid",
    gridTemplateColumns: gridCols,
    padding: "12px 16px",
    borderBottom: "1px solid #f0f0f0",
    background: bg,
    alignItems: "start",
    transition: "background 0.2s",
  });

  return (
    <Card
      title={
        <div className="d-flex align-items-center gap-2">
          <TeamOutlined style={{ color: "#1677ff", fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>Attorney Details</span>
          <Tag color="blue">{existing.length} active</Tag>
          {planLimit > 0 && (
            <Tag color="purple">Plan limit: {planLimit}</Tag>
          )}
        </div>
      }
      extra={
        validNewSlots.length > 0 && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={updateMutation.isPending}
            onClick={handleSave}
            style={{
              background: "linear-gradient(135deg,#667eea,#764ba2)",
              border: "none",
              fontWeight: 600,
            }}
          >
            Save {validNewSlots.length} New Attorney{validNewSlots.length > 1 ? "s" : ""}
          </Button>
        )
      }
      className="shadow-sm mb-4"
      style={{ borderRadius: 12 }}
      styles={{ body: { padding: 0 } }}
    >
      {/* ── Table header ── */}
      <div style={headerRowStyle}>
        <div>#</div>
        <div>Attorney Number</div>
        <div style={{ textAlign: "center" }}>TM Count</div>
        <div style={{ textAlign: "center" }}>Status</div>
        <div style={{ textAlign: "center" }}>Action</div>
      </div>

      {/* ── Existing read-only rows ── */}
      {existing.map((atty, index) => (
        <div key={`ex-${index}`} style={rowStyle("#ffffff")}>
          {/* # */}
          <div style={{ color: "#8c8c8c", fontWeight: 600, fontSize: 13, paddingTop: 2 }}>
            {index + 1}
          </div>

          {/* Attorney Number + name chips */}
          <div>
            <div className="d-flex align-items-center gap-2">
              <FileProtectOutlined style={{ color: "#1677ff" }} />
              <strong style={{ fontSize: 14 }}>{atty.attorney_number}</strong>
              <Tooltip title="Saved — the number itself cannot be edited">
                <LockOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
              </Tooltip>
            </div>
            {atty.attorney_names.length > 0 && (
              <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                {atty.attorney_names.map((name) => (
                  <div
                    key={name}
                    style={{
                      padding: "3px 8px",
                      borderRadius: 5,
                      fontSize: 11,
                      fontWeight: 600,
                      border: "1.5px solid #52c41a",
                      background: "#f6ffed",
                      color: "#389e0d",
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* TM Count */}
          <div style={{ textAlign: "center", paddingTop: 2 }}>
            <Tag color={atty.tm_count > 0 ? "green" : "default"} style={{ fontWeight: 600, minWidth: 60 }}>
              {Number(atty.tm_count).toLocaleString("en-IN")}
            </Tag>
          </div>

          {/* Status */}
          <div style={{ textAlign: "center", paddingTop: 2 }}>
            <Tag icon={<CheckCircleOutlined />} color="success">ACTIVE</Tag>
          </div>

          {/* Action.

              No "Locked" tag beside a Delete button — the two would contradict
              each other. The lock icon by the number already says the number
              cannot be edited; removing it is a separate thing, and allowed. */}
          <div style={{ textAlign: "center", paddingTop: 2 }}>
            <Tooltip title="Delete this attorney number">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleting === atty.attorney_number}
                disabled={Boolean(deleting) && deleting !== atty.attorney_number}
                onClick={() => confirmDelete(atty)}
              >
                Delete
              </Button>
            </Tooltip>
          </div>
        </div>
      ))}

      {/* ── Empty slot rows ── */}
      {!isCorporate &&
        slots.map((slot, index) => {
          const rowNum = existing.length + index + 1;
          const isValid = slot.status === "valid";
          const isInvalid = slot.status === "invalid";
          const nameOpts = slotNameOptions[index] || [];
          const selNames = slotSelectedNames[index] || [];

          return (
            <div
              key={`slot-${index}`}
              style={rowStyle(isValid ? "#f6ffed" : isInvalid ? "#fff1f0" : "#fafafa")}
            >
              {/* # */}
              <div style={{ color: "#8c8c8c", fontWeight: 600, fontSize: 13, paddingTop: 2 }}>
                {rowNum}
              </div>

              {/* Input + name chips */}
              <div>
                <Input
                  value={slot.value}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  placeholder={`Enter attorney number ${rowNum}`}
                  size="middle"
                  maxLength={10}
                  style={{
                    maxWidth: 220,
                    borderColor: isValid ? "#52c41a" : isInvalid ? "#ff4d4f" : undefined,
                    borderWidth: slot.status ? 2 : undefined,
                  }}
                  status={isInvalid ? "error" : undefined}
                  suffix={
                    slot.isChecking ? (
                      <Spin size="small" />
                    ) : isValid ? (
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    ) : isInvalid ? (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    ) : (
                      <InfoCircleOutlined style={{ color: "#bfbfbf" }} />
                    )
                  }
                  onPressEnter={() => handleCheckStatus(index)}
                />
                {slot.message && (
                  <div style={{ fontSize: 11, marginTop: 3, color: isValid ? "#52c41a" : "#ff4d4f", fontWeight: 500 }}>
                    {isValid ? "✓" : "✗"} {slot.message}
                  </div>
                )}
                {isValid && nameOpts.length > 0 &&
                  renderNameChips(nameOpts, selNames, (name) =>
                    setSlotSelectedNames((prev) => {
                      const cur = prev[index] || [];
                      const exists = cur.includes(name);
                      return { ...prev, [index]: exists ? cur.filter((n) => n !== name) : [...cur, name] };
                    })
                  )
                }
              </div>

              {/* TM Count */}
              <div style={{ textAlign: "center", paddingTop: 2 }}>
                {isValid ? (
                  <Tag color="green" style={{ fontWeight: 600, minWidth: 60 }}>
                    {Number(slot.tmCount).toLocaleString("en-IN")}
                  </Tag>
                ) : (
                  <span style={{ color: "#d9d9d9" }}>—</span>
                )}
              </div>

              {/* Status */}
              <div style={{ textAlign: "center", paddingTop: 2 }}>
                {isValid ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">VALID</Tag>
                ) : isInvalid ? (
                  <Tag icon={<CloseCircleOutlined />} color="error">INVALID</Tag>
                ) : (
                  <Tag color="default">EMPTY</Tag>
                )}
              </div>

              {/* Check Status button */}
              <div style={{ textAlign: "center", paddingTop: 2 }}>
                <Button
                  icon={<SearchOutlined />}
                  size="small"
                  loading={slot.isChecking}
                  disabled={!slot.value.trim() || slot.isChecking}
                  onClick={() => handleCheckStatus(index)}
                  style={{
                    background: isValid
                      ? "linear-gradient(135deg,#52c41a,#73d13d)"
                      : "linear-gradient(135deg,#722ed1,#9254de)",
                    color: "#fff",
                    border: "none",
                    fontWeight: 600,
                    borderRadius: 6,
                  }}
                >
                  {isValid ? "Re-check" : "Check Status"}
                </Button>
              </div>
            </div>
          );
        })}

      {/* Corporate: no slots needed */}
      {isCorporate && existing.length === 0 && (
        <div
          className="text-center py-4 m-3 rounded"
          style={{ background: "linear-gradient(135deg,#f0f5ff,#d6e4ff)", border: "2px solid #91d5ff" }}
        >
          <span style={{ fontSize: 36, color: "#1890ff", fontWeight: 700 }}>{userData?.total_tm_count ?? 5}</span>
          <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 4 }}>
            Default TM Count — Corporate plans include {userData?.total_tm_count ?? 5} TM{(userData?.total_tm_count ?? 5) !== 1 ? "s" : ""} by default
          </div>
        </div>
      )}

      {/* No plan / no slots */}
      {!isCorporate && existing.length === 0 && slots.length === 0 && (
        <div className="text-center py-5">
          <TeamOutlined style={{ fontSize: 52, color: "#d9d9d9" }} />
          <p className="text-muted mt-3 mb-0">
            No attorney slots available.
            <br />
            <span style={{ fontSize: 12 }}>
              Go to the <strong>Subscription</strong> tab to configure the plan.
            </span>
          </p>
        </div>
      )}

      {/* ── Stats bar ── */}
      {(existing.length > 0 || validNewSlots.length > 0) && (
        <div
          className="m-4 p-3 rounded"
          style={{ background: "linear-gradient(135deg,#f0f7ff 0%,#e8f4fd 100%)", border: "1px solid #bae0ff" }}
        >
          <div className="row text-center g-0">
            <div className="col-4" style={{ borderRight: "1px solid #bae0ff" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#1677ff" }}>{existing.length}</div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>Active Attorneys</div>
            </div>
            <div className="col-4" style={{ borderRight: "1px solid #bae0ff" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#722ed1" }}>{slots.length}</div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>Empty Slots</div>
            </div>
            <div className="col-4">
              <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>
                {Number(totalTMCount).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>Total TM Count</div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AttorneyDetails;
